/* Cart Accessories – production handler
   - Clicks call window.SF_addAccessory(this) from snippet buttons
   - Uses theme refresh events with { detail: { opener } } for focus-safe redraw
   - Blocks rapid multi-clicks (inflight Set)
   - Optional dataLayer push
   - Fallbacks are lightweight and only used if you toggle ALLOW_FALLBACK
*/
(function () {
  if (window.SF_addAccessory) return; // guard

  const ALLOW_FALLBACK = false; // set true if you want section-fetch fallback as a backstop
  const inflight = new Set();

  function getSectionHandle() {
    return (
      document.querySelector('.cart-accessories-container')?.dataset.drawerSectionHandle ||
      'cart-drawer'
    );
  }

  async function addVariant(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    if (!res.ok) throw await res.json().catch(() => ({}));
    return res.json();
  }

  async function addVariantWithRetry(id, tries = 1) {
    try {
      return await addVariant(id);
    } catch (e) {
      if (tries > 0) return addVariantWithRetry(id, tries - 1);
      throw e;
    }
  }

  async function fetchCartJSON() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      return r.ok ? r.json() : null;
    } catch {
      return null;
    }
  }

  async function updateCartCount() {
    const cart = await fetchCartJSON();
    if (!cart) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
    });
  }

  function announce(message) {
    const live = document.getElementById('a11y-cart-message');
    if (live) live.textContent = message;
  }

  function replaceDrawerWith(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newDrawer =
      doc.querySelector('cart-drawer') ||
      doc.querySelector('[data-cart-drawer]') ||
      doc.body.firstElementChild;
    const current =
      document.querySelector('cart-drawer') || document.querySelector('[data-cart-drawer]');
    if (newDrawer && current) {
      current.replaceWith(newDrawer);
      return true;
    }
    return false;
  }

  async function fallbackReplaceDrawer() {
    const SECTION_HANDLE = getSectionHandle();
    const urls = [
      location.pathname + '?sections=' + encodeURIComponent(SECTION_HANDLE),
      location.pathname + '?section_id=' + encodeURIComponent(SECTION_HANDLE)
    ];
    for (const url of urls) {
      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) continue;
        const text = await res.text();
        try {
          const obj = JSON.parse(text);
          const html = obj && obj[SECTION_HANDLE];
          if (html && replaceDrawerWith(html)) return true;
        } catch {
          if (replaceDrawerWith(text)) return true;
        }
      } catch {}
    }
    return false;
  }

  async function refreshDrawerWithTheme(openerEl) {
    try {
        const drawer = document.querySelector('cart-drawer') || document.querySelector('[data-cart-drawer]');
        // Always ask theme to refresh
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
        bubbles: true,
        detail: { opener: openerEl || document.body }
        }));
        // Only open if it's currently closed
        const isClosed = !drawer || drawer.getAttribute('aria-hidden') === 'true';
        if (isClosed) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
            bubbles: true,
            detail: { opener: openerEl || document.body }
        }));
        }
        return true;
    } catch {
        return false;
    }
    }


  async function ensureDrawerUpdated(openerEl) {
    await refreshDrawerWithTheme(openerEl);
    // Light sanity: update count regardless; theme usually updates too.
    setTimeout(updateCartCount, 150);

    if (ALLOW_FALLBACK) {
      // Optional hard fallback if your theme refresh ever fails in edge cases
      setTimeout(async () => {
        await fallbackReplaceDrawer();
        await updateCartCount();
      }, 400);
    }
  }

  function disableButton(btn, busy) {
    const t = btn.querySelector('.btn-text');
    const l = btn.querySelector('.btn-loading');
    if (t && l) {
      t.style.display = busy ? 'none' : 'inline-block';
      l.style.display = busy ? 'inline-block' : 'none';
    }
    btn.disabled = !!busy;
  }

  function optimisticRemoveCard(btn) {
    const card = btn.closest('.cart-accessory-item');
    if (!card) return;
    card.style.opacity = '0.5';
    card.style.pointerEvents = 'none';
    setTimeout(() => {
      if (card.parentNode) card.parentNode.removeChild(card);
      if (!document.querySelector('.cart-accessory-item')) {
        const container = document.querySelector('.cart-accessories-container');
        if (container) container.style.display = 'none';
      }
    }, 120);
  }

  window.SF_addAccessory = function (btn) {
    const variantId = btn?.dataset?.variantId;
    const productId = btn?.dataset?.productId;
    if (!variantId) return;

    // prevent rapid double-click across buttons for the same variant
    if (inflight.has(variantId)) return;
    inflight.add(variantId);

    disableButton(btn, true);

    addVariantWithRetry(variantId, 1)
      .then(() => {
        // Optional analytics
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'accessory_add_to_cart',
            item_variant_id: variantId,
            item_product_id: productId
          });
        } catch {}

        announce('Accessory added to cart. Cart updated.');
        optimisticRemoveCard(btn);
        return ensureDrawerUpdated(btn);
      })
      .catch((err) => {
        const msg =
          (err && (err.description || err.message)) ||
          'Could not add to cart. Please try again.';
        // Replace this alert with your theme’s toast if available
        // e.g., window.Theme?.toast(msg)
        alert(msg);
        try {
          document.dispatchEvent(new CustomEvent('on:cart:error', { detail: { error: err } }));
        } catch {}
      })
      .finally(() => {
        disableButton(btn, false);
        inflight.delete(variantId);
      });
  };
})();
